import { useCallback, useEffect, useRef, useState } from "react";

/** Maximum length of a selection we'll act on — longer selections are likely
 * accidental "select all" gestures and aren't useful for word/phrase actions. */
const MAX_SELECTION_LENGTH = 200;
/** Debounce for keyboard-driven (Shift+Arrow) selection settling. */
const DEBOUNCE_MS = 250;

/** Attribute opting an element subtree out of selection handling: our own
 * floating UI (toolbar, note dialog) carries this so interacting with it never
 * collapses the user's selection nor re-triggers evaluation. */
export const SELECTION_TOOLBAR_IGNORE_ATTR = "data-selection-toolbar-ignore";

interface VirtualElement {
  getBoundingClientRect: () => DOMRect;
}

export interface UseTextSelectionReturn {
  /** The committed, trimmed selection text (empty when nothing is selected). */
  text: string;
  /** Whether the toolbar should be shown for the current selection. */
  isOpen: boolean;
  /** Stable virtual anchor for Radix `PopoverAnchor` — tracks the selection rect. */
  virtualRef: React.MutableRefObject<VirtualElement>;
  /** Imperatively close the toolbar (e.g. after an action runs). */
  dismiss: () => void;
}

/** Walk ancestors to decide whether a selection should be ignored: it sits
 * inside an editable control (native selection UX), or inside our own UI. */
const isEditableOrIgnored = (node: Node | null): boolean => {
  let el: Element | null =
    node instanceof Element ? node : node?.parentElement ?? null;
  while (el) {
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if ((el as HTMLElement).isContentEditable) return true;
    if (el.hasAttribute(SELECTION_TOOLBAR_IGNORE_ATTR)) return true;
    el = el.parentElement;
  }
  return false;
};

const isInIgnoredUI = (target: EventTarget | null): boolean => {
  const el = target instanceof Element ? target : null;
  return !!el?.closest(`[${SELECTION_TOOLBAR_IGNORE_ATTR}]`);
};

/**
 * Detects user text highlighting anywhere in the document and exposes the
 * selected text plus a virtual anchor rect for a floating toolbar. All DOM and
 * selection bookkeeping lives here so the consuming component stays purely
 * presentational. See {@link SELECTION_TOOLBAR_IGNORE_ATTR} for opting subtrees out.
 */
export const useTextSelection = (): UseTextSelectionReturn => {
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const rectRef = useRef<DOMRect | null>(null);
  const pointerDownRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Identity must stay stable across renders or Radix loses the anchor; only
  // rectRef.current is mutated.
  const virtualRef = useRef<VirtualElement>({
    getBoundingClientRect: () => rectRef.current ?? new DOMRect(),
  });

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setText("");
    rectRef.current = null;
  }, []);

  const evaluate = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      setIsOpen(false);
      return;
    }

    const trimmed = selection.toString().trim();
    if (!trimmed || trimmed.length > MAX_SELECTION_LENGTH) {
      setIsOpen(false);
      return;
    }

    const range = selection.getRangeAt(0);
    if (isEditableOrIgnored(range.commonAncestorContainer)) {
      setIsOpen(false);
      return;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setIsOpen(false);
      return;
    }

    rectRef.current = rect;
    setText(trimmed);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const onSelectionChange = () => {
      clearTimeout(debounceRef.current);
      // Mid-drag: wait for pointerup (mouse) or debounce settle (keyboard).
      if (pointerDownRef.current) return;
      debounceRef.current = setTimeout(evaluate, DEBOUNCE_MS);
    };

    const onPointerDown = (e: PointerEvent) => {
      // Clicks inside our own UI must not collapse/re-evaluate the selection.
      if (isInIgnoredUI(e.target)) return;
      pointerDownRef.current = true;
      // A fresh click outside our UI dismisses any open toolbar; if it begins a
      // new selection, pointerup re-opens.
      setIsOpen(false);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (isInIgnoredUI(e.target)) return;
      pointerDownRef.current = false;
      // Re-evaluate once the browser has finalized the drag selection.
      clearTimeout(debounceRef.current);
      setTimeout(evaluate, 0);
    };

    const onScroll = () => {
      // The anchor rect is viewport-relative and goes stale on scroll.
      setIsOpen((open) => (open ? false : open));
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };

    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearTimeout(debounceRef.current);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [evaluate, dismiss]);

  return { text, isOpen, virtualRef, dismiss };
};
